import React, { useState, useMemo, useEffect } from "react";
import { Badge, Button } from "reactstrap";
import ReactTable from "components/ReactTable/ReactTable.js";
import ReactTable2 from "components/ReactTable/ReactTable2.js";
import { useNavigate } from "react-router-dom";
import { formatDateForDisplay } from "../../../utils/dateUtils";
import { resolveDealListReceivedDate } from "../../../utils/dealListDates";
import { useTranslation } from "react-i18next";
import { useGetTenants } from "api/tenant/service";
import { useGetAllSADeals } from "api/super-admin/service";
import classNames from "classnames";
import SearchDealsFilters from "../../deals/components/SearchDealsFilters";
import SearchDealsCards from "../../deals/components/SearchDealsCards";
import "../../deals/SearchDeals.css";

const formatDisplayText = (text) => {
  if (!text) return "";
  return text
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

const Deals = ({ activeColor = "blue", shellThemeVariant = "1" }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState([]);
  const [tenant, setTenant] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [expandedRows, setExpandedRows] = useState({});
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const { data: tenantsData, isLoading: isLoadingTenants } = useGetTenants(1, 1000);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(handler);
  }, [search]);

  const statusOptions = useMemo(
    () => [
      { value: "working", label: t("searchDeals.working") },
      { value: "prospect", label: t("searchDeals.prospect") },
      { value: "contracted", label: "Contracted" },
      { value: "spot_delivered", label: "Spot Delivered" },
      { value: "cancelled", label: "Cancelled" },
      { value: "contracted_booked", label: "Contracted/Booked" },
    ],
    [t]
  );

  const statusArray = useMemo(
    () => status.map((option) => option.value),
    [status]
  );

  const { data: deals, isLoading } = useGetAllSADeals(
    debouncedSearch,
    statusArray,
    page,
    pageSize,
    tenant
  );

  const serverPageCount = deals?.pagination?.pageCount;
  useEffect(() => {
    if (serverPageCount == null || serverPageCount < 1) return;
    if (page > serverPageCount) {
      setPage(serverPageCount);
    }
  }, [serverPageCount, page]);

  const isTheme2 = shellThemeVariant === "2";
  const DealsTable = isTheme2 ? ReactTable2 : ReactTable;

  const tenantOptions = useMemo(
    () =>
      (tenantsData?.data || tenantsData?.results || []).map((row) => ({
        value: String(row.code),
        label: row.name,
      })) || [],
    [tenantsData]
  );

  const tenantValue = useMemo(
    () =>
      tenant
        ? {
            value: tenant,
            label:
              tenantOptions.find((opt) => String(opt.value) === String(tenant))?.label || tenant,
          }
        : null,
    [tenant, tenantOptions]
  );

  const dealColumns = useMemo(
    () => [
      {
        Header: t("searchInventory.tenant"),
        accessor: "tenant_name",
        width: 100,
        minWidth: 100,
        maxWidth: 100,
        Cell: ({ value }) => (
          <div style={{ fontWeight: "600", color: "#1a6fbf", fontSize: "13px" }}>{value}</div>
        ),
      },
      {
        Header: t("searchDeals.dealId"),
        accessor: "deal_id",
        width: 80,
        minWidth: 80,
        maxWidth: 80,
      },
      {
        Header: t("searchDeals.receivedDate"),
        accessor: "received_date",
        width: 120,
        minWidth: 120,
        maxWidth: 120,
      },
      {
        Header: t("searchDeals.applicantDetails"),
        accessor: "applicant_details",
        className: "applicant-details-column",
        minWidth: 500,
        width: 500,
        maxWidth: 500,
        Cell: ({ value, row }) => {
          const rowId = row.id;
          const isExpanded = expandedRows[rowId];
          const worksheets = value?.worksheets || [];
          const hasMoreThan1 = worksheets.length > 1;

          return (
            <div>
              {value?.name && (
                <div
                  style={{
                    fontWeight: "600",
                    fontSize: "13px",
                    marginBottom: "6px",
                    color: "#1a6fbf",
                  }}
                >
                  {value.name}
                </div>
              )}
              {worksheets
                .slice(0, isExpanded ? worksheets.length : Math.min(1, worksheets.length))
                .map((worksheet) => (
                  <div
                    className="worksheet-container"
                    style={{
                      display: "flex",
                      alignItems: "flex-end",
                      border: "0.5px solid",
                      padding: "10px",
                      justifyContent: "space-between",
                      marginBottom: "10px",
                      width: "100%",
                    }}
                    key={worksheet.worksheet_id}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <div style={{ fontWeight: "bold", fontSize: "13px" }}>
                        {worksheet.type === "local_lender_finance"
                          ? worksheet?.finance_company?.name || worksheet?.local_lender?.name
                          : worksheet.type === "cash"
                            ? t("searchDeals.cash")
                            : worksheet.type === "wholesale"
                              ? t("searchDeals.wholesale")
                              : t("searchDeals.inHouse")}
                      </div>
                      <div style={{ marginLeft: "10px", textAlign: "left" }}>
                        <div style={{ fontSize: "13px" }}>
                          {worksheet.inventory?.inventory_vehicle?.year}{" "}
                          {worksheet.inventory?.inventory_vehicle?.make}{" "}
                          {worksheet.inventory?.inventory_vehicle?.model}
                        </div>
                        <div style={{ fontWeight: "bold", fontSize: "12px" }}>
                          {worksheet.contract_date ? "Contract Date:" : ""}
                        </div>
                        <div style={{ fontSize: "12px" }}>
                          {formatDateForDisplay(worksheet.contract_date || "-")}
                        </div>
                        <div style={{ fontSize: "12px" }}>
                          {worksheet.working_date ? "Working Date:" : ""}
                        </div>
                        <div style={{ fontSize: "12px" }}>
                          {formatDateForDisplay(worksheet.working_date || "-")}
                        </div>
                      </div>
                    </div>
                    <div
                      style={{
                        textAlign: "right",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "flex-end",
                        alignItems: "flex-end",
                        height: "100%",
                        gap: "4px",
                      }}
                    >
                      <span style={{ fontSize: "13px", fontWeight: "600", color: "#1a6fbf" }}>
                        {formatDisplayText(worksheet.worksheet_status)}
                      </span>
                      <div style={{ fontSize: "11px", opacity: 0.7 }}>
                        {formatDateForDisplay(worksheet?.status_change_date)}
                      </div>
                    </div>
                  </div>
                ))}
              {hasMoreThan1 && (
                <div style={{ position: "relative", zIndex: 1000 }}>
                  <Button
                    style={{
                      position: "relative",
                      zIndex: 1001,
                      background: "transparent",
                      border: "none",
                    }}
                    color="link"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setExpandedRows((prev) => ({
                        ...prev,
                        [rowId]: !prev[rowId],
                      }));
                    }}
                  >
                    <i className={`fas fa-chevron-${isExpanded ? "up" : "down"}`}></i>
                  </Button>
                </div>
              )}
            </div>
          );
        },
      },
      {
        Header: t("searchDeals.createdBy"),
        accessor: "created_user_full_name",
        width: 150,
        minWidth: 150,
        maxWidth: 150,
      },
      {
        Header: () => (
          <div style={{ textAlign: "center", width: "100%" }}>
            {t("searchDeals.status")} / {t("searchDeals.flag")}
          </div>
        ),
        accessor: "status_flag",
        width: 200,
        minWidth: 200,
        maxWidth: 200,
        Cell: ({ value }) => (
          <div
            style={{
              textAlign: "center",
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
              gap: "8px",
            }}
          >
            {value?.status}
            {value?.flag}
          </div>
        ),
      },
    ],
    [t, expandedRows]
  );

  const dealData = useMemo(() => {
    const list = deals?.data || deals?.results || deals || [];
    const rows = Array.isArray(list) ? list : [];
    return rows.map((item) => ({
      id: item.deal_id,
      deal_id: item.deal_id || "",
      tenant_name: item.tenant?.name || "-",
      tenant_id: item.tenant?.id || "",
      received_date: formatDateForDisplay(resolveDealListReceivedDate(item)),
      applicant_details: { name: item?.full_name || item?.wholesaler_name || "", worksheets: item?.worksheets || [] },
      created_user_full_name: item.created_user_full_name || "",
      status_flag: {
        status: (
          <Badge
            color={
              item.deal_status === "working"
                ? "success"
                : item.deal_status === "prospect"
                  ? "info"
                  : item.deal_status === "completed"
                    ? "success"
                    : "danger"
            }
          >
            {formatDisplayText(item.deal_status)}
          </Badge>
        ),
        flag: item.is_have_flags ? (
          <Badge color="success">{t("searchDeals.yes")}</Badge>
        ) : (
          <Badge color="danger">{t("searchDeals.no")}</Badge>
        ),
      },
    }));
  }, [deals, t]);

  const filters = (
    <SearchDealsFilters
      activeColor={activeColor}
      isTheme2={isTheme2}
      search={search}
      onSearchChange={setSearch}
      status={status}
      onStatusChange={setStatus}
      dealType=""
      onDealTypeChange={() => {}}
      statusOptions={statusOptions}
      dealTypeOptions={[]}
      showDealTypeFilter={false}
      showTenantFilter
      onTenantChange={(value) => {
        setTenant(value);
        setPage(1);
      }}
      tenantValue={tenantValue}
      tenantOptions={tenantOptions}
      isLoadingTenants={isLoadingTenants}
      tenantLabel={t("searchInventory.tenant")}
    />
  );

  const table = (
    <div
      className="inventory-table-wrap table-scroll-wrapper"
      style={{ fontSize: isTheme2 ? "13px" : "12px", minWidth: 0, maxWidth: "100%" }}
    >
      <DealsTable
        activeColor={activeColor}
        data={dealData}
        columns={dealColumns}
        className={classNames("-striped -highlight", { "sticky-stock-table": isTheme2 })}
        manualPagination={true}
        pageCount={deals?.pagination?.pageCount || 1}
        pageIndex={page - 1}
        totalItems={deals?.pagination?.total}
        pageSize={pageSize}
        noDataText={t("searchDeals.noRecordsFound")}
        loading={isLoading}
        loadingText={t("searchDeals.loading")}
        onPageChange={(pageIndex) => setPage(pageIndex + 1)}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        onRowClick={(row) => {
          const tenantQuery = row.tenant_id ? `?tenantId=${row.tenant_id}` : "";
          navigate(`/deals/${row.deal_id}${tenantQuery}`);
        }}
      />
    </div>
  );

  return (
    <div
      className={classNames("content", "search-deals", "search-deals--sa", {
        "search-deals--theme2": isTheme2,
        "theme2-admin-content-compact": isTheme2,
        "theme2-search-deals-inset": isTheme2,
      })}
    >
      <SearchDealsCards
        isTheme2={isTheme2}
        filters={filters}
        table={table}
        title={`Super Admin - ${t("searchDeals.title")}`}
      />
    </div>
  );
};

export default Deals;
